import { API_PATH, BASE_PATH } from './config';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './HamburgerMenu.css';

const HamburgerMenu = ({ isLoggedIn, user, onLogout, onLogin, onShowAccountLinking, onShowProfileSettings, currentTripId, onShareTrip }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = React.useState(false);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const handleLogout = () => {
        toggleMenu(); // Close menu on action
        onLogout();
    };

    const handleLogin = () => {
        toggleMenu(); // Close menu on action
        onLogin();
    };

    const handleAccountLinking = () => {
        toggleMenu(); // Close menu on action
        if (onShowAccountLinking) {
            onShowAccountLinking();
        }
    };

    const handleProfileSettings = () => {
        toggleMenu(); // Close menu on action
        if (onShowProfileSettings) {
            onShowProfileSettings();
        }
    };

    const handleShareTrip = () => {
        toggleMenu(); // Close menu on action
        if (onShareTrip && currentTripId) {
            onShareTrip(currentTripId);
        }
    };

    const handleExpenses = () => {
        toggleMenu(); // Close menu
        if (currentTripId) {
            // If already on a trip page, scroll to expenses section
            const expensesElement = document.getElementById('expenses-section');
            if (expensesElement) {
                expensesElement.scrollIntoView({ behavior: 'smooth' });
            } else {
                // Navigate to trip page with hash
                window.location.href = `${BASE_PATH}/trip/${currentTripId}#expenses`;
            }
        } else {
            // Navigate to home page
            window.location.href = `${BASE_PATH}/`;
        }
    };

    // Determine verification status
    const isVerified = user?.is_verified || user?.has_whatsapp || (user?.has_email && user?.email_verified);
    const isGuest = user?.is_guest;
    const isAdmin = user?.public_user_id === '7BY9N';

    const overlayClasses = `hamburger-menu-overlay ${isOpen ? 'open' : ''}`;
    const sidebarClasses = `hamburger-sidebar ${isOpen ? 'open' : ''}`;

    const portalTarget = React.useMemo(() => {
        if (typeof document !== 'undefined') {
            return document.body;
        }
        return null;
    }, []);

    const menuPortal = portalTarget ? createPortal(
        (
            <>
                <div className={overlayClasses} onClick={toggleMenu}></div>
                <div className={sidebarClasses}>
                    {isLoggedIn && user && (
                        <>
                            {/* Verification Status Banner */}
                            <div className={`verification-banner ${isVerified ? 'verified' : 'unverified'}`}>
                                <span className="verification-icon">{isVerified ? '✓' : '!'}</span>
                                <span className="verification-text">
                                    {isGuest ? t('account_guest') : (isVerified ? t('account_verified') : t('account_unverified'))}
                                </span>
                            </div>
                            
                            <div className="user-profile-section">
                                <div className="user-avatar">
                                    <div className="avatar-circle">
                                        {user.whatsapp_name ? user.whatsapp_name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                </div>
                                <div className="user-info">
                                    <div className="user-name">{user.whatsapp_name || 'User'}</div>
                                    {/* Display User ID for sharing */}
                                    {user.public_user_id && (
                                        <div className="user-id-display">
                                            <span className="user-id-label">{t('user_id_label')}</span>
                                            <span className="user-id-value">{user.public_user_id}</span>
                                        </div>
                                    )}
                                    {/* Username credential */}
                                    {user.username && !user.username.includes('@whatsapp.user') && (
                                        <div className="user-phone">✉️ {user.username}</div>
                                    )}
                                    {/* WhatsApp credential */}
                                    {user.has_whatsapp && user.phone_number ? (
                                        <div className="user-phone">📱 {user.phone_number}</div>
                                    ) : (
                                        <div className="user-phone" style={{ color: '#999' }}>📵 {t('whatsapp_not_linked')}</div>
                                    )}
                                    <div className="user-status">{t('logged_in_status')}</div>
                                </div>
                            </div>
                        </>
                    )}
                    <ul>
                        {isLoggedIn && user ? (
                            <>
                                {/* Profile Settings */}
                                <li><button onClick={handleProfileSettings}>{t('profile_settings')}</button></li>
                                {/* Share This Trip - only when viewing a trip itinerary */}
                                {currentTripId && (
                                    <li><button onClick={handleShareTrip}>{t('share_this_trip')}</button></li>
                                )}
                                {/* Memories - only when viewing a trip */}
                                {currentTripId && (
                                    <li><a href={`${BASE_PATH}/trip/${currentTripId}/memories`} onClick={toggleMenu}>📸 {t('memories') || 'Memories'}</a></li>
                                )}
                                {/* Expenses - always available, personal expenses that can be assigned to a trip */}
                                <li><a href={`${BASE_PATH}/expenses`} onClick={toggleMenu}>💵 {t('expenses') || 'Expenses'}</a></li>
                                {/* Trip Page moved to the top bar for quicker access */}
                                <li><a href={`${BASE_PATH}/archived`} onClick={toggleMenu}>{t('archived_trips')}</a></li>
                                <li><a href={`${BASE_PATH}/flight-search`} onClick={toggleMenu}>{t('flight_search')}</a></li>
                                <li><a href={`${BASE_PATH}/help`} onClick={toggleMenu}>❓ Help</a></li>
                                {/* Admin Console - only visible to admin user */}
                                {isAdmin && (
                                    <>
                                        <li><a href={`${BASE_PATH}/admin`} onClick={toggleMenu}>🔧 Admin Console</a></li>
                                        <li><a href={`${BASE_PATH}/admin?tab=user-management`} onClick={toggleMenu}>👥 User Management</a></li>
                                        <li><a href={`${BASE_PATH}/admin?tab=statistics`} onClick={toggleMenu}>📈 Statistics</a></li>
                                        <li><a href={`${BASE_PATH}/admin?tab=feature-usage`} onClick={toggleMenu}>📊 Feature Usage</a></li>
                                        <li><a href={`${BASE_PATH}/admin?tab=server-monitor`} onClick={toggleMenu}>📊 Server Monitor</a></li>
                                    </>
                                )}
                                <li><button onClick={handleLogout}>{t('logout')}</button></li>
                            </>
                        ) : (
                            <li><button onClick={handleLogin}>{t('login_whatsapp')}</button></li>
                        )}
                    </ul>
                </div>
            </>
        ),
        portalTarget
    ) : null;

    return (
        <>
            <div className="hamburger-icon" onClick={toggleMenu}>
                <div></div>
                <div></div>
                <div></div>
            </div>
            {menuPortal}
        </>
    );
};

export default HamburgerMenu;